class SmileatSubcollection extends HTMLElement {
    connectedCallback() {
        this.container = this.querySelector('.smileat-subcollection__list');

        if (this.container) {
            this.initDrag();
        }

        this.initSticky();
        this.setSubcollectionHeight();
        
        window.addEventListener('resize', this.setSubcollectionHeight.bind(this));
    }

    disconnectedCallback() {
        if (this.handleScroll) {
            window.removeEventListener('scroll', this.handleScroll);
        }
    }

    initSticky() {
        this.handleScroll = () => {
            const section = this.closest('.section-subcollection');
            if (!section) return;

            const flowTop = this.cachedFlowTop || 0;
            const originalHeight = this.originalHeight || 0;
            
            // Check if section bottom has fully scrolled out of screen natively
            const isStuck = window.scrollY > (flowTop + originalHeight);
            
            if (this.isStuck !== isStuck) {
                this.isStuck = isStuck;
                if (isStuck) {
                    section.style.boxSizing = 'border-box';
                    section.style.height = `${originalHeight}px`;
                } else {
                    section.style.height = '';
                    section.style.boxSizing = '';
                }
                document.documentElement.classList.toggle('subcollection-sticky', isStuck);
                
                // Allow CSS layout change (hiding images) to physically apply, 
                // then update the variable to the new small height!
                requestAnimationFrame(() => {
                    this.setSubcollectionHeight();
                });
            }
        };

        window.addEventListener('scroll', this.handleScroll, { passive: true });
        // Initial check
        window.requestAnimationFrame(() => {
            this.setSubcollectionHeight();
            this.handleScroll();
        });
    }

    setSubcollectionHeight() {
        const section = this.closest('.section-subcollection');
        if (!section) return;

        if (!document.documentElement.classList.contains('subcollection-sticky')) {
            this.originalHeight = section.offsetHeight;
            document.documentElement.style.setProperty('--subcollection-height', '0px');
            
            let flowTop = 0;
            let el = section;
            while(el) {
                flowTop += el.offsetTop;
                el = el.offsetParent;
            }
            this.cachedFlowTop = flowTop;
        } else {
            // When sticky, the inner element is small (images hidden). Set the variable to this small height!
            document.documentElement.style.setProperty('--subcollection-height', `${this.offsetHeight}px`);
        }
    }

    initDrag() {
        const el = this.container;
        if (!el) return;

        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;
        let hasDragged = false;
        let velocity = 0;
        let lastX = 0;
        let animFrame;

        const applyMomentum = () => {
            if (Math.abs(velocity) < 0.5) return;
            const maxScroll = el.scrollWidth - el.clientWidth;
            const next = el.scrollLeft - velocity;
            if (next <= 0 || next >= maxScroll) {
                el.scrollLeft = Math.max(0, Math.min(next, maxScroll));
                return; // stop at edges
            }
            el.scrollLeft = next;
            velocity *= 0.92;
            animFrame = requestAnimationFrame(applyMomentum);
        };

        const onMouseDown = (e) => {
            cancelAnimationFrame(animFrame);
            isDown = true;
            hasDragged = false;
            velocity = 0;
            startX = e.pageX - el.getBoundingClientRect().left;
            lastX = e.pageX;
            scrollLeft = el.scrollLeft;
        };

        const onMouseUp = () => {
            if (!isDown) return;
            isDown = false;
            el.classList.remove('is-dragging');
            applyMomentum();
        };

        const onMouseMove = (e) => {
            if (!isDown) return;
            const x = e.pageX - el.getBoundingClientRect().left;
            const walk = (x - startX) * 1.5;
            
            if (Math.abs(walk) > 10) {
                if (!hasDragged) {
                    hasDragged = true;
                    el.classList.add('is-dragging');
                }
                e.preventDefault();
                velocity = (e.pageX - lastX) * 1.5;
                lastX = e.pageX;
                el.scrollLeft = scrollLeft - walk;
            }
        };

        // Unified click handler
        el.addEventListener('click', (e) => {
            if (hasDragged) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, true);

        el.addEventListener('mousedown', onMouseDown);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousemove', onMouseMove);

        // Clean up any ongoing JS animation if user touches the element natively
        el.addEventListener('touchstart', () => {
            cancelAnimationFrame(animFrame);
            isDown = false;
            hasDragged = false;
        }, { passive: true });
    }
}

customElements.define('smileat-subcollection-component', SmileatSubcollection);
